(async () => {
  if (!customElements.get('product-media-slider')) {
    const { Swiper } = await import('swiper');
    const { A11y, Navigation, Pagination, Thumbs } = await import('swiper/modules');
    const { Fancybox } = await import('@fancyapps/ui');

    Swiper.use([Navigation, Pagination, A11y, Thumbs]);

    class ProductMediaSlider extends HTMLElement {
      constructor() {
        super();

        // Get swiper element and define the slides that are global or variant images for both the main and the thumbs
        this.swiperMain = this.querySelector('[data-product-media-slider]');
        this.swiperMainAllSlides = this.swiperMain.querySelectorAll('.swiper-slide');
        this.swiperMainVariantSlides = Array.from(this.swiperMainAllSlides).filter(
          (slide) => slide.dataset.productVariantImage,
        );
        this.swiperMainGlobalSlides = Array.from(this.swiperMainAllSlides).filter(
          (slide) => !slide.dataset.productVariantImage,
        );

        this.swiperThumbs = this.querySelector('[data-product-media-thumbnails-slider]');
        this.swiperThumbAllSlides = this.swiperThumbs.querySelectorAll('.swiper-slide');
        this.swiperThumbVariantSlides = Array.from(this.swiperThumbAllSlides).filter(
          (slide) => slide.dataset.productVariantThumb,
        );
        this.swiperThumbGlobalSlides = Array.from(this.swiperThumbAllSlides).filter(
          (slide) => !slide.dataset.productVariantThumb,
        );

        // Check if we have extra options
        if (this && this.dataset && this.dataset.options) {
          this.options = JSON.parse(this.dataset.options);
        }

        // Create elements
        this.thumbSlider();
        this.mainSlider();
        this.fancybox();

        // Reduce actions (So the event can also be removed)
        this.reducer = {
          updateVariant: (e) => this.updateVariant(e),
        };

        // Listen for variant change and go to slide
        document.addEventListener('variant-change', this.reducer.updateVariant);
      }

      /*
       * Create thumb slider
       */
      thumbSlider() {
        // Check if Swiper exists
        if (!Swiper) return false;

        // Get Thumbnails slider element & Set swiper options
        this.swiperThumbsOptions = {
          a11y: true,
          spaceBetween: 4,
          observer: true,
          threshold: 10,
          slidesPerView: 'auto',
          watchSlidesProgress: true,
          slidesOffsetAfter: 6,
        };

        // show/hide all the relevant images and init the swiper
        this.displaySlides(
          this.swiperThumbVariantSlides,
          this.swiperThumbGlobalSlides,
          this.swiperThumbs,
          'productVariantThumb',
        );
        this.swiperThumbsInstance = new Swiper(this.swiperThumbs, this.swiperThumbsOptions);

        // Rebuild Swiper in design mode after delay
        if (Shopify.designMode && this.swiperThumbsInstance) {
          window.addEventListener('shopify:section:load', () => {
            setTimeout(() => {
              this.swiperThumbsInstance.update();
            }, 300);
          });
        }
      }

      /*
       * Create main slider
       */
      mainSlider() {
        // Check if Swiper exists
        if (!Swiper) return false;

        // re-init the thumbs in case they were destroyed
        (!this.swiperThumbsInstance || this.swiperThumbsInstance.destroyed) && this.thumbSlider();

        // Get thumb options
        let thumbOptions;
        if (this.swiperThumbsInstance) {
          thumbOptions = {
            thumbs: {
              swiper: this.swiperThumbsInstance,
            },
          };
        }

        // Swiper options
        this.swiperMainOptions = {
          a11y: true,
          observer: true,
          threshold: 10,
          pagination: {
            el: '.swiper-pagination-main',
            clickable: true,
          },
          navigation: {
            nextEl: '.swiper-button-main-next',
            prevEl: '.swiper-button-main-prev',
          },
          ...thumbOptions,
        };

        // show/hide all the relevant images and init the swiper
        this.displaySlides(
          this.swiperMainVariantSlides,
          this.swiperMainGlobalSlides,
          this.swiperMain,
          'productVariantImage',
        );
        this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);

        // Rebuild Swiper in design mode
        if (Shopify.designMode && this.swiperMainInstance) {
          window.addEventListener('shopify:section:load', () => {
            this.swiperMainInstance.update();
          });
        }
      }

      updateSlides() {
        if (!this.options.currentSku || !this.options.hasVariants) return;

        // destroy the existing swipers
        if (this.swiperMainInstance) this.swiperMainInstance.destroy();
        if (this.swiperThumbsInstance) this.swiperThumbsInstance.destroy();

        // show/hide all the relevant images
        this.displaySlides(
          this.swiperMainVariantSlides,
          this.swiperMainGlobalSlides,
          this.swiperMain,
          'productVariantImage',
        );
        this.displaySlides(
          this.swiperThumbVariantSlides,
          this.swiperThumbGlobalSlides,
          this.swiperThumbs,
          'productVariantThumb',
        );

        // update the swipers
        (!this.swiperMainInstance || this.swiperMainInstance.destroyed) && this.mainSlider();
      }

      /**
       *
       * @param {Object} parent is a DOM element
       * @param {String} dataAttr is data attribute string to check for

       * This function creates new slides and hides the original ones. This way, the only slides are visible ones, not hidden ones.
       */
      displaySlides(slides, globalSlides, parentEl, dataAttr) {
        const newSlides = [];

        slides.forEach((slide, index) => {
          // if the slide is the current variant, remove the class and push it to the new array
          if (slide.dataset[dataAttr] === this.options.currentSku) {
            slide.classList.remove('hidden');

            // for the main images, add the fancybox link
            if (dataAttr === 'productVariantImage' && slide.querySelector('a')) {
              slide.querySelector('a').dataset.fancybox = 'product-media-slider';
              slide.querySelector('a').setAttribute.tabIndex = 0;
            }

            newSlides.push(slide);
          } else {
            slide.classList.add('hidden');
            if (dataAttr === 'productVariantImage' && slide.querySelector('a')) {
              slide.querySelector('a').removeAttribute('data-fancybox');
              slide.querySelector('a').setAttribute.tabIndex = -1;
            }
          }
          // when is last loop, append the new slides to the wrappers
          if (index === slides.length - 1 && newSlides.length) {
            // clear the wrapper
            parentEl.querySelector('.swiper-wrapper').innerHTML = '';

            // append the slides
            [...newSlides, ...globalSlides].forEach((slide) => {
              parentEl.querySelector('.swiper-wrapper').appendChild(slide);
            });
          }
        });
      }

      /*
       * Update variant
       * @Param e {object}: event object with variant
       */
      updateVariant(e) {
        if (e?.detail?.variant?.sku) {
          this.options.currentSku = e.detail.variant.sku;
          this.updateSlides();
        }
      }

      /*
       * Create fancybox
       */
      fancybox() {
        // Check if Fancybox exists
        if (typeof Fancybox == 'undefined') {
          return false;
        }

        Fancybox.bind('[data-fancybox]', {
          animated: false,
          showClass: false,
          on: {
            ready: (fancybox) => {
              trapFocus(fancybox.$container);
            },
            destroy: () => {
              removeTrapFocus();
            },
          },
        });
      }
    }

    window.ProductMediaSlider = ProductMediaSlider;
    customElements.define('product-media-slider', ProductMediaSlider);
  }
})();
